import styled from '@emotion/styled';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import { useEffect } from 'react';
import { segment } from '@actions/analytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { getMe } from '@api/selectors/auth';
import { StyledButton } from './Form';

const Title = styled(H2)`
  margin-bottom: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const TitileName = styled(H2)`
  margin-bottom: ${Spacings.SPACING_2B};
  font-weight: normal;
  font-size: ${Fonts.H3};
`;

const Card = styled.div`
  position: relative;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_8B};
`;

const Disclaimer = styled(P1)`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const StyledImage = styled.img`
  height: ${Spacings.SPACING_25B};
  width: ${Spacings.SPACING_25B};
  top: 0px;
  right: 0px;
  position: absolute;
  border-radius: ${Spacings.SPACING_2B};
`;

const Divider = styled.hr`
  margin: ${Spacings.SPACING_6B} 0px;
  border-top: 1px solid var(--color-divider);
`;

const ScoreText = styled.p`
  color: var(--color-text-secondary);
`;

const RankInfo = styled.div`
  font-size: ${Fonts.H2};
  font-weight: ${Fonts.SEMIBOLD};
  margin-top: ${Spacings.SPACING_3B};
`;

const TotalInfo = styled.div`
  font-size: ${Fonts.H3};
  font-weight: ${Fonts.SEMIBOLD};
  margin-top: ${Spacings.SPACING_3B};
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: baseline;
`;

const ScoreCard = ({ predictedPercentile, marks, title }) => {
  return (
    <>
      <ScoreText>{title}</ScoreText>
      <ScoreDisplay>
        <RankInfo>{predictedPercentile}</RankInfo>
        <ScoreText>&nbsp;%ile for</ScoreText>
        <TotalInfo>&nbsp;{marks}</TotalInfo>
        <ScoreText>&nbsp;marks</ScoreText>
      </ScoreDisplay>
    </>
  );
};

const Result = ({
  predictedPercentile,
  setShowResult,
  setPredictedPercentile
}) => {
  const { goalInfo } = useGoalInfo('XNDUS');
  const myInfo = useMyInfo(getMe);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    segment.track('RANK PREDICTION', {
      user_uid: myInfo?.uid,
      goal_name: goalInfo?.name,
      goal_uid: 'XNDUS',
      exam_uid: 'XNDUS',
      score: predictedPercentile?.overAllMarks,
      predicted_percentile: predictedPercentile?.overAllPercentile
    });
  }, [
    goalInfo?.name,
    myInfo?.uid,
    predictedPercentile?.overAllMarks,
    predictedPercentile?.overAllPercentile
  ]);

  const onPredictAgain = () => {
    setPredictedPercentile(null);
    setShowResult(false);
  };

  return (
    <>
      <TitileName>{myInfo?.name},</TitileName>
      <Title>Your predicted CAT Percentile</Title>
      <ScoreDisplay>
        <ScoreText>for a score of &nbsp;</ScoreText>
        <TotalInfo>{predictedPercentile?.overAllMarks}</TotalInfo>
        <ScoreText>, your percentile might be</ScoreText>
      </ScoreDisplay>
      <Card>
        <StyledImage
          src={getImage('rankpredictor/rank.svg')}
          height={60}
          width={60}
        />
        <ScoreText>Overall Percentile</ScoreText>
        <ScoreDisplay>
          <RankInfo>{predictedPercentile?.overAllPercentile}</RankInfo>
          <ScoreText>&nbsp;%ile</ScoreText>
        </ScoreDisplay>
        <Divider />
        <ScoreCard
          title="VARC percentile"
          predictedPercentile={predictedPercentile?.verbalPercentile}
          marks={predictedPercentile?.verbalMarks}
        />
        <Divider />
        <ScoreCard
          title="LRDI percentile"
          predictedPercentile={predictedPercentile?.lrdiPercentile}
          marks={predictedPercentile?.lrdiMarks}
        />
        <Divider />
        <ScoreCard
          title="QUANT percentile"
          predictedPercentile={predictedPercentile?.quantPercentile}
          marks={predictedPercentile?.quantMarks}
        />
      </Card>
      <Disclaimer>
        Disclaimer: The estimated rank is based on past years results data of
        the aspirants
      </Disclaimer>
      <>
        <StyledButton
          label="Predict again"
          onClick={onPredictAgain}
          $colorType="light_dark"
        />
      </>
    </>
  );
};

export default Result;
